import { type InputHTMLAttributes, useId } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { useDisconnectedState } from "@virtual-brain-lib/frontend-design-system/components";

export const switchVariants = tv({
	slots: {
		wrapper:
			"inline-flex cursor-pointer items-center rounded-full transition-all duration-200 ease-in-out has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1",
		track: "relative inline-flex cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out",
		thumb: "pointer-events-none inline-block cursor-pointer rounded-full bg-white transition-transform duration-200 ease-in-out",
		input: "absolute h-0 w-0 cursor-pointer opacity-0",
	},
	variants: {
		size: {
			sm: {
				track: "h-5 w-9",
				thumb: "h-4 w-4 data-[checked=false]:translate-x-0.5 data-[checked=true]:translate-x-[1.125rem]",
			},
			md: {
				track: "h-6 w-11",
				thumb: "h-5 w-5 data-[checked=false]:translate-x-0.5 data-[checked=true]:translate-x-[1.375rem]",
			},
			lg: {
				track: "h-7 w-14",
				thumb: "h-6 w-6 data-[checked=false]:translate-x-0.5 data-[checked=true]:translate-x-[1.875rem]",
			},
		},
		color: {
			primary: {
				wrapper: "has-[:focus-visible]:outline-primary-active-ring",
				track: "data-[checked=false]:bg-primary-hover-2 data-[checked=true]:bg-primary-active",
			},
			danger: {
				wrapper: "has-[:focus-visible]:outline-danger-active-ring",
				track: "data-[checked=false]:bg-danger-hover-2 data-[checked=true]:bg-danger-active",
			},
			info: {
				wrapper: "has-[:focus-visible]:outline-info-active-ring",
				track: "data-[checked=false]:bg-info-hover-2 data-[checked=true]:bg-info-active",
			},
			success: {
				wrapper: "has-[:focus-visible]:outline-success-active-ring",
				track: "data-[checked=false]:bg-success-hover-2 data-[checked=true]:bg-success-active",
			},
			warning: {
				wrapper: "has-[:focus-visible]:outline-warning-active-ring",
				track: "data-[checked=false]:bg-warning-hover-2 data-[checked=true]:bg-warning-active",
			},
		},
		disabled: {
			true: {
				wrapper: "cursor-not-allowed opacity-60",
				track: "cursor-not-allowed",
			},
		},
	},
	defaultVariants: {
		size: "md",
		color: "primary",
	},
});

type SwitchVariants = VariantProps<typeof switchVariants>;

export type SwitchProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "color"> &
	SwitchVariants & {
		// eslint-disable-next-line react/boolean-prop-naming
		readonly checked: boolean;
		readonly onCheckedChange?: (checked: boolean) => void;
	};

export function Switch({
	checked,
	onCheckedChange,
	size = "md",
	color = "primary",
	disabled: propsDisabled = false,
	className,
	id: propsId,
	...props
}: SwitchProps) {
	const { isDisconnected } = useDisconnectedState();
	const disabled = isDisconnected ?? propsDisabled;
	const { wrapper, track, thumb, input } = switchVariants({ size, color, disabled });

	const idRandom = useId();
	const id = propsId ?? idRandom;

	const handleChange = () => {
		if (!disabled && onCheckedChange) {
			onCheckedChange(!checked);
		}
	};

	return (
		<div className={wrapper({ className })}>
			<input
				type="checkbox"
				className={input()}
				checked={checked}
				disabled={disabled}
				id={id}
				onChange={handleChange}
				onKeyDown={(e) => {
					if (e.key === "Enter" && !disabled) {
						e.preventDefault();
						handleChange();
					}
				}}
				{...props}
			/>
			<label htmlFor={id} className={track()} data-checked={checked}>
				<span className={thumb()} data-checked={checked} />
			</label>
		</div>
	);
}
